import { v4 } from 'uuid';
import {
  AdditionalPersonWithId,
  IPolicyHolderDetails,
  IQuote,
  IReviewSummary,
  IReviewSummaryItem,
  ISitecoreReviewSummaryItem,
} from '@/interfaces';
import {
  joinAddressComponents,
  joinAddressComponentsForPolicyholder,
  joinIQPolicyholderResidentialAddress,
  mapPolicyholder,
  reversePolicyholder,
  reversePolicyholderPostalAddress,
} from '@/mappers';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { OrdinalNumbers } from '@/constants/ordinal-numbers.const';
import { toSpacedContactNumber } from '@/utils/regex-helpers';

/**
 * Map additional policyholder from form data to quote
 *
 * @param quote
 * @param formData
 * @param policyholderIndex
 */
export const mapAdditionalPolicyholdersFormToQuote = (
  quote: Partial<IQuote>,
  formData: Record<string, any>,
  policyholderIndex: number,
  uuid?: string
) => {
  if (!quote.additionalPolicyHolderDetails) quote.additionalPolicyHolderDetails = [];

  const uniqueId = uuid ?? v4();
  const policyholder =
    quote.additionalPolicyHolderDetails[policyholderIndex] || ({ policyHolderId: uniqueId } as IPolicyHolderDetails);
  mapPolicyholder(policyholder, formData);

  if (!quote.additionalPolicyHolderDetails[policyholderIndex]) quote.additionalPolicyHolderDetails.push(policyholder);
  else quote.additionalPolicyHolderDetails[policyholderIndex] = policyholder;
};

/**
 * Map additional policyholder from quote to form data
 *
 * @param quote
 * @param formData
 * @param policyholderIndex
 */
export const mapQuoteToAdditionalPolicyholdersForm = (
  quote: Partial<IQuote>,
  formData: Record<string, any>,
  policyholderIndex: number,
  postalAddressCompId: string
): void => {
  const additionalPolicyholder = quote.additionalPolicyHolderDetails?.[policyholderIndex];

  // Nothing to map if addl policyholder doesn't exist and no address copying is needed
  if (!additionalPolicyholder) {
    return;
  }

  reversePolicyholder(additionalPolicyholder, formData, {
    postalAddressCompId,
  });
};

export const reversePrimaryPolicyHolderAddress = (
  policyholder: IPolicyHolderDetails,
  formData: Record<string, any>,
  autoAddressDefaultText: string = 'Not on list - I will enter manually'
) => {
  if (policyholder?.street.value) {
    formData.postalAddress_is_manual = !policyholder?.dpid.value;
    formData.postalAddress = reversePolicyholderPostalAddress(policyholder);

    if (!formData.postalAddress_is_manual) {
      formData.postalAddress_auto = joinAddressComponents(formData.postalAddress);
    } else {
      formData.postalAddress_auto = autoAddressDefaultText;
    }
  }
};

/**
 * Adds additional people steps to form store dynamically.
 *
 * @param additionalPeople - Array of additional people or policyholders
 * @param formStore - The form store holding current step state
 * @param stepId - The step ID representing the additional people section
 * @param getUuid - Function to derive a UUID from the given person
 */
export const addAdditionalPeopleSteps = (
  additionalPeople: AdditionalPersonWithId[],
  formStore: any,
  stepId: string,
  getUuid: (person: AdditionalPersonWithId) => string = (person) =>
    person?.policyholderDetails?.policyHolderId ??
    person?.driverDetails?.driverId ??
    person?.policyHolderId ??
    person?.driverId ??
    v4()
) => {
  const totalPeople = additionalPeople.length;
  const scopedPeopleCount = formStore.inScopeSteps.filter((step: { id: string }) => step.id === stepId).length;

  const shouldAddSteps = totalPeople > scopedPeopleCount || (scopedPeopleCount === 0 && totalPeople === 0);

  if (!shouldAddSteps) return;

  const iterations = totalPeople > 0 ? totalPeople : 1;
  let insertAfterIndex = formStore.currentStepIndex + 1;

  for (let i = 0; i < iterations; i++) {
    const person = additionalPeople[i];
    const uuid = getUuid(person);

    const alreadyExists = formStore.inScopeSteps.some((step: { uuid: string }) => step.uuid === uuid);
    if (!alreadyExists) {
      formStore.addStepToScope(stepId, {
        removable: true,
        afterIndex: insertAfterIndex,
        isVisible: true,
        uuid,
      });
      insertAfterIndex++;
    }
  }
};

export const mapAdditionalPeopleSummaries = (
  formInputs: any,
  quote: IQuote,
  titleSuffix: string,
  fieldId: string,
  isConfirmationStep: boolean = false
): IReviewSummary[] => {
  const addlPersonSummary = formInputs.reviewSummary.fields.filter((x: any) => x.formInputId?.value === fieldId);
  return quote.additionalPolicyHolderDetails.map((person, index) => ({
    formContentId: person?.policyHolderId ?? v4(),
    summaryTitle: { value: `${OrdinalNumbers[index]} ${titleSuffix}` },
    icon: { value: addlPersonSummary?.at(0)?.icon?.value },
    items: mapAdditionalPersonToReviewSummaryItems(
      person,
      addlPersonSummary,
      person?.policyHolderId,
      isConfirmationStep
    ),
  }));
};

export const mapAdditionalPersonToReviewSummaryItems = (
  person: AdditionalPersonWithId,
  additionalPeopleSummaryItem: any,
  stepUuid: string,
  isConfirmationStep: boolean = false
): IReviewSummaryItem[] => {
  if (!person) return [];

  const policyholderSummary = {
    policyholderName:
      `${person?.salutation?.value ?? ''} ${person?.firstname?.value ?? ''} ${person?.surname?.value ?? ''}`.trim(),
    contactNumber: toSpacedContactNumber(person?.telephoneNumber?.value ?? ''),
    postalAddress: `${joinAddressComponentsForPolicyholder(person as IPolicyHolderDetails, true, false)}`,
    residentialAddress: joinIQPolicyholderResidentialAddress((person as IPolicyHolderDetails)?.residentialAddress),
  };

  if (!additionalPeopleSummaryItem || additionalPeopleSummaryItem.length == 0) {
    return mapAdditionalPersonToDefaultReviewSummaryItems(policyholderSummary, stepUuid);
  }

  const addlPolicyholderSummaryItem = replaceBookmarks(additionalPeopleSummaryItem, policyholderSummary);

  return addlPolicyholderSummaryItem[0].items
    .map((item: ISitecoreReviewSummaryItem) => {
      const result: IReviewSummaryItem = {
        title: item.fields.title,
        content: item.fields.content,
        editUrl: stepUuid,
        icon: { value: '' },
        showItem: true,
        showEdit: !isConfirmationStep,
      };
      return result;
    })
    .filter((item: any) => item.content?.value);
};

const mapAdditionalPersonToDefaultReviewSummaryItems = (
  policyholderSummary: any,
  uuid: string
): IReviewSummaryItem[] => {
  if (!policyholderSummary) return [];

  const reviewItems: IReviewSummaryItem[] = [
    {
      title: 'Name',
      content: policyholderSummary.policyholderName,
    },
    { title: 'Contact details', content: policyholderSummary.contactNumber },
    {
      title: 'Postal address',
      content: policyholderSummary.postalAddress,
    },
  ]
    .filter((item) => item.content) // Remove empty items
    .map((item) => ({
      title: { value: item.title },
      content: { value: item.content },
      editUrl: uuid,
      icon: { value: '' },
      showItem: true,
      showEdit: true,
    }));

  return reviewItems;
};
